import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, FileText, Layers, ToggleLeft, ToggleRight } from 'lucide-react';
import {
  fetchAdminPages,
  fetchAdminSection,
  fetchAdminSections,
  patchAdminSection,
  type AdminPageSummary,
  type AdminSectionRow,
} from '../../api/admin';
import {
  AdminDetailCard,
  AdminEmptyState,
  AdminKpiStrip,
  AdminPageLayout,
  AdminSearchInput,
  AdminStatusBadge,
  AdminTablePanel,
} from '../../components/admin/AdminPageLayout';
import { Illustration } from '../../components/common/Illustration';
import { LoadingState, ErrorState } from '../../components/states/StateViews';

function PageCard({
  page,
  onSelect,
}: {
  page: AdminPageSummary;
  onSelect: () => void;
}) {
  return (
    <button type="button" className="wfx-admin-page-card glass-surface" onClick={onSelect}>
      <Illustration context={page.illustration} size="sm" className="wfx-admin-page-card-illustration" />
      <div className="wfx-admin-page-card-copy">
        <strong>{page.label}</strong>
        <span className="wfx-muted">{page.route}</span>
      </div>
      <div className="wfx-admin-page-card-stats">
        <span><Layers size={14} aria-hidden="true" /> {page.sectionCount} sections</span>
        <span>{page.enabledCount} enabled</span>
        {page.disabledCount > 0 && <span className="wfx-admin-page-card-warn">{page.disabledCount} off</span>}
      </div>
    </button>
  );
}

function SectionDetailCard({
  sectionId,
  pages,
  onClose,
}: {
  sectionId: string | null;
  pages: AdminPageSummary[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-section', sectionId],
    queryFn: () => fetchAdminSection(sectionId!),
    enabled: Boolean(sectionId),
  });

  const update = useMutation({
    mutationFn: (body: Partial<AdminSectionRow>) => patchAdminSection(sectionId!, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-sections'] });
      void qc.invalidateQueries({ queryKey: ['admin-section', sectionId] });
      void qc.invalidateQueries({ queryKey: ['admin-pages'] });
    },
  });

  const section = data?.section;
  const configPreview = section?.configuration
    ? JSON.stringify(section.configuration, null, 2)
    : '{}';
  const illustration = section
    ? (pages.find((page) => page.id === section.page)?.illustration ?? 'admin.module.cms')
    : 'admin.module.cms';

  return (
    <AdminDetailCard
      open={Boolean(sectionId)}
      onClose={onClose}
      ariaLabel="Section details"
      breadcrumb="CMS section"
      title={section?.title ?? section?.sectionTypeLabel ?? sectionId ?? 'Loading…'}
      illustration={illustration}
      subtitle={
        section ? (
          <>
            <AdminStatusBadge status={section.isEnabled ? 'enabled' : 'disabled'} />
            <span className="wfx-muted wfx-admin-subline">{section.id}</span>
          </>
        ) : undefined
      }
    >
      {isLoading && <LoadingState label="Loading section…" />}
      {isError && (
        <ErrorState message="Could not load section." onRetry={() => void refetch()} />
      )}

      {section && (
        <>
          <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
            <div><dt>Page</dt><dd>{section.pageLabel ?? section.page}</dd></div>
            <div><dt>Type</dt><dd>{section.sectionTypeLabel ?? section.sectionType}</dd></div>
            <div><dt>Order</dt><dd>{section.displayOrder}</dd></div>
            <div>
              <dt>Public route</dt>
              <dd>
                {section.pageRoute && (
                  <Link to={section.pageRoute} className="secondary-action compact" target="_blank">
                    {section.pageRoute}
                    <ExternalLink size={12} aria-hidden="true" />
                  </Link>
                )}
              </dd>
            </div>
          </dl>

          <div className="wfx-admin-drawer-columns">
            <div className="wfx-admin-drawer-section">
              <h3>Edit section</h3>
              <div className="wfx-admin-form">
                <label className="wfx-admin-field">
                  <span>Title</span>
                  <input
                    defaultValue={section.title ?? ''}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (section.title ?? '')) update.mutate({ title: value || null });
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Subtitle</span>
                  <input
                    defaultValue={section.subtitle ?? ''}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (section.subtitle ?? '')) update.mutate({ subtitle: value || null });
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Display order</span>
                  <input
                    type="number"
                    defaultValue={section.displayOrder}
                    onBlur={(e) => {
                      const value = Number(e.target.value);
                      if (Number.isFinite(value) && value !== section.displayOrder) {
                        update.mutate({ displayOrder: value });
                      }
                    }}
                  />
                </label>
                <button
                  type="button"
                  className="secondary-action compact"
                  disabled={update.isPending}
                  onClick={() => update.mutate({ isEnabled: !section.isEnabled })}
                >
                  {section.isEnabled ? (
                    <><ToggleRight size={16} aria-hidden="true" /> Disable section</>
                  ) : (
                    <><ToggleLeft size={16} aria-hidden="true" /> Enable section</>
                  )}
                </button>
              </div>
            </div>

            <div className="wfx-admin-drawer-section">
              <h3>Configuration</h3>
              <pre className="wfx-admin-config-preview">{configPreview}</pre>
            </div>
          </div>
        </>
      )}
    </AdminDetailCard>
  );
}

export function AdminPagesScreen() {
  const [query, setQuery] = useState('');
  const [pageFilter, setPageFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);

  const { data: pagesData } = useQuery({
    queryKey: ['admin-pages'],
    queryFn: fetchAdminPages,
  });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-sections', pageFilter, statusFilter, typeFilter, query],
    queryFn: () =>
      fetchAdminSections({
        page: pageFilter,
        status: statusFilter,
        sectionType: typeFilter,
        q: query || undefined,
      }),
  });

  const pages = pagesData?.pages ?? [];
  const sections = data?.data ?? [];
  const stats = data?.stats ?? pagesData?.stats;

  const sectionTypes = useMemo(() => {
    const types = new Set<string>();
    for (const page of pages) {
      for (const type of page.sectionTypes) types.add(type);
    }
    return Array.from(types).sort();
  }, [pages]);

  const kpiItems = useMemo(
    () => [
      { label: 'Pages', value: stats?.pages ?? pages.length },
      { label: 'Sections', value: stats?.total ?? 0 },
      { label: 'Enabled', value: stats?.enabled ?? 0 },
      { label: 'Disabled', value: stats?.disabled ?? 0 },
    ],
    [stats, pages.length],
  );

  if (isLoading && !data) return <LoadingState label="Loading pages…" />;
  if (isError) return <ErrorState message="Could not load CMS pages." onRetry={() => void refetch()} />;

  return (
    <>
      <AdminPageLayout
        breadcrumb="Admin / Content / Pages"
        title="Pages"
        description="Manage CMS page sections across the public site — heroes, grids, banners, and more."
        icon={FileText}
        illustration="admin.module.cms"
      >
        <AdminKpiStrip items={kpiItems} />

        <div className="wfx-admin-page-grid">
          {pages.map((page) => (
            <PageCard
              key={page.id}
              page={page}
              onSelect={() => setPageFilter(page.id)}
            />
          ))}
        </div>

        <AdminTablePanel
          title="Page sections"
          toolbar={(
            <div className="wfx-admin-toolbar">
              <AdminSearchInput
                value={query}
                onChange={setQuery}
                placeholder="Search sections…"
              />
              <select
                className="wfx-admin-select"
                value={pageFilter}
                onChange={(e) => setPageFilter(e.target.value)}
                aria-label="Filter by page"
              >
                <option value="all">All pages</option>
                {pages.map((page) => (
                  <option key={page.id} value={page.id}>{page.label}</option>
                ))}
              </select>
              <select
                className="wfx-admin-select"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                aria-label="Filter by type"
              >
                <option value="all">All types</option>
                {sectionTypes.map((type) => (
                  <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
                ))}
              </select>
              <select
                className="wfx-admin-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Filter by status"
              >
                <option value="all">All statuses</option>
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
          )}
        >
          <table className="wfx-admin-table">
            <thead>
              <tr>
                <th>Page</th>
                <th>Type</th>
                <th>Title</th>
                <th>Order</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {sections.map((row) => (
                <tr
                  key={row.id}
                  className="wfx-admin-table-row-clickable"
                  onClick={() => setSelectedSectionId(row.id)}
                >
                  <td>
                    <strong>{row.pageLabel ?? row.page}</strong>
                    <span className="wfx-muted wfx-admin-subline">{row.pageRoute}</span>
                  </td>
                  <td>{row.sectionTypeLabel ?? row.sectionType}</td>
                  <td>{row.title ?? row.id}</td>
                  <td>{row.displayOrder}</td>
                  <td><AdminStatusBadge status={row.isEnabled ? 'enabled' : 'disabled'} /></td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="chip-pill"
                      onClick={() => setSelectedSectionId(row.id)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {sections.length === 0 && <AdminEmptyState message="No sections match your filters." />}
        </AdminTablePanel>
      </AdminPageLayout>

      <SectionDetailCard
        sectionId={selectedSectionId}
        pages={pages}
        onClose={() => setSelectedSectionId(null)}
      />
    </>
  );
}
